import { DOCUMENT_PREFIX, parseDocumentContext } from './chatDocumentContext.mjs';

// Shared by the composer, authenticated BFF and private gateway. No storage or remote fetching.
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_CHAT_BODY_BYTES = 8_000_000; // Exclusive; native Hermes accepts 10 MB.
export const MAX_IMAGES = 4;
const MAX_DATA_URL_LENGTH = Math.ceil(MAX_IMAGE_BYTES / 3) * 4 + 32;
const MESSAGES = {
  chat_image_invalid: 'Choose a valid PNG, JPEG or WebP image, not a link or file path.',
  chat_image_unsupported: 'Only PNG, JPEG and WebP images are supported. No SVG or GIF.',
  chat_images_too_many: 'Attach at most 4 images per message.',
  chat_images_too_large: 'Images must total 5 MiB or less per message.',
  chat_image_dimensions: 'Images must be at most 8,192 pixels per side and 20 million pixels.',
  chat_body_too_large: 'This message is too large. Reduce the images or text.',
  chat_body_invalid: 'The message could not be read. Try again.',
};
export class ChatImageError extends Error {
  constructor(code, status = 400) { super(MESSAGES[code] || MESSAGES.chat_image_invalid); this.code = code; this.status = status; }
}
const invalid = () => { throw new ChatImageError('chat_image_invalid'); };
const object = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const keysOnly = (value, keys) => object(value) && Object.keys(value).every((key) => keys.includes(key));

// Rendering is intentionally limited to inline raster URLs, even for caller-supplied UI rows.
export function safeImageDataUrl(value) {
  if (typeof value !== 'string' || value.length > MAX_DATA_URL_LENGTH) return null;
  const header = /^data:image\/(png|jpeg|webp);base64,/.exec(value);
  if (!header) return null;
  const data = value.slice(header[0].length);
  if (!data || data.length % 4 || /[^A-Za-z0-9+/=]/.test(data)
    || data.indexOf('=') !== -1 && !/^[^=]*={1,2}$/.test(data)) return null;
  return value;
}
function dimensions(width, height) {
  if (!width || !height || width > 8192 || height > 8192 || width * height > 20_000_000) {
    throw new ChatImageError('chat_image_dimensions');
  }
  return [width, height];
}
function rasterDimensions(bytes, mime) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const ascii = (start, length) => String.fromCharCode(...bytes.subarray(start, start + length));
  if (mime === 'png') {
    if (bytes.length < 45 || ascii(0, 8) !== '\x89PNG\r\n\x1a\n' || view.getUint32(8) !== 13 || ascii(12, 4) !== 'IHDR') invalid();
    const result = dimensions(view.getUint32(16), view.getUint32(20));
    let hasData = false;
    for (let offset = 8; offset + 12 <= bytes.length;) {
      const length = view.getUint32(offset), kind = ascii(offset + 4, 4);
      if (offset + length + 12 > bytes.length) invalid();
      if (kind === 'acTL') throw new ChatImageError('chat_image_unsupported');
      if (kind === 'IDAT' && length > 0) hasData = true;
      offset += length + 12;
      if (kind === 'IEND') {
        if (length || offset !== bytes.length || !hasData) invalid();
        return result;
      }
    }
    invalid();
  }
  if (mime === 'jpeg') {
    if (bytes.length < 12 || bytes[0] !== 255 || bytes[1] !== 216 || bytes.at(-2) !== 255 || bytes.at(-1) !== 217) invalid();
    let result;
    for (let offset = 2; offset + 4 <= bytes.length;) {
      if (bytes[offset++] !== 255) invalid();
      while (bytes[offset] === 255) offset++;
      const marker = bytes[offset++];
      if (offset + 2 > bytes.length) invalid();
      const length = view.getUint16(offset);
      if (length < 2 || offset + length > bytes.length) invalid();
      if ([0xc0, 0xc1, 0xc2].includes(marker)) {
        if (length < 8) invalid();
        result = dimensions(view.getUint16(offset + 5), view.getUint16(offset + 3));
      }
      if (marker === 0xda) { if (!result || offset + length >= bytes.length - 2) invalid(); return result; }
      offset += length;
    }
    invalid();
  }
  if (mime === 'webp') {
    if (bytes.length < 26 || ascii(0, 4) !== 'RIFF' || ascii(8, 4) !== 'WEBP' || view.getUint32(4, true) !== bytes.length - 8) invalid();
    let frame, canvas;
    for (let offset = 12; offset + 8 <= bytes.length;) {
      const kind = ascii(offset, 4), length = view.getUint32(offset + 4, true), start = offset + 8;
      if (start + length + (length % 2) > bytes.length) invalid();
      if (kind === 'ANIM' || kind === 'ANMF') throw new ChatImageError('chat_image_unsupported');
      if (kind === 'VP8X') {
        if (length !== 10) invalid();
        if (bytes[start] & 2) throw new ChatImageError('chat_image_unsupported');
        const u24 = (i) => bytes[i] + bytes[i + 1] * 256 + bytes[i + 2] * 65536;
        canvas = dimensions(1 + u24(start + 4), 1 + u24(start + 7));
      } else if (kind === 'VP8 ') {
        if (length < 10 || (bytes[start] & 1) || ascii(start + 3, 3) !== '\x9d\x01\x2a') invalid();
        frame = dimensions(view.getUint16(start + 6, true) & 0x3fff, view.getUint16(start + 8, true) & 0x3fff);
      } else if (kind === 'VP8L') {
        if (length < 5 || bytes[start] !== 0x2f || bytes[start + 4] >> 5) invalid();
        frame = dimensions(1 + ((bytes[start + 1] | bytes[start + 2] << 8) & 0x3fff), 1 + ((bytes[start + 2] >> 6 | bytes[start + 3] << 2 | bytes[start + 4] << 10) & 0x3fff));
      }
      offset = start + length + (length % 2);
      if (offset === bytes.length && frame) {
        if (canvas && (canvas[0] !== frame[0] || canvas[1] !== frame[1])) invalid();
        return frame;
      }
    }
    invalid();
  }
  invalid();
}
export function validateImages(value) {
  if (value === undefined) return [];
  if (!Array.isArray(value)) invalid();
  if (value.length > MAX_IMAGES) throw new ChatImageError('chat_images_too_many');
  let total = 0;
  return value.map((image) => {
    if (!keysOnly(image, ['name', 'dataUrl']) || typeof image.name !== 'string' || !image.name.trim() || image.name.length > 255 || typeof image.dataUrl !== 'string') invalid();
    const { name, dataUrl } = image;
    if (dataUrl.length > MAX_DATA_URL_LENGTH) throw new ChatImageError('chat_images_too_large', 413);
    if (dataUrl.startsWith('data:') && !/^data:image\/(png|jpeg|webp);base64,/.test(dataUrl)) throw new ChatImageError('chat_image_unsupported');
    if (!safeImageDataUrl(dataUrl)) invalid();
    const [header, encoded] = dataUrl.split(',');
    let binary;
    try { binary = atob(encoded); } catch { invalid(); }
    if (btoa(binary) !== encoded) invalid();
    total += binary.length;
    if (total > MAX_IMAGE_BYTES) throw new ChatImageError('chat_images_too_large', 413);
    rasterDimensions(Uint8Array.from(binary, (c) => c.charCodeAt(0)), header.slice(11, -7));
    return { name, dataUrl };
  });
}

/** Native content is allowed only after BFF derivation, never as a browser input. */
export function validateNativeImageMessage(body) {
  if (!object(body) || 'images' in body || 'attachments' in body || 'documents' in body) invalid();
  const context = parseDocumentContext(Array.isArray(body.message) ? body.message[0]?.text : body.message);
  const validText = (text) => typeof text === 'string' && text.trim()
    && (text.startsWith(DOCUMENT_PREFIX) ? Boolean(context) : text.length <= 8000);
  if (!Array.isArray(body.message)) {
    if (!validText(body.message)) invalid();
    return;
  }
  const images = [];
  for (const [index, part] of body.message.entries()) {
    if (keysOnly(part, ['type', 'text']) && part.type === 'text' && index === 0 && validText(part.text)) continue;
    if (!keysOnly(part, ['type', 'image_url']) || part.type !== 'image_url' || !keysOnly(part.image_url, ['url'])) invalid();
    images.push({ name: 'attached-image', dataUrl: part.image_url.url });
  }
  if (!images.length || images.length + (context?.documents.length || 0) > MAX_IMAGES) invalid();
  validateImages(images);
}

/** Count actual bytes while reading, not string length after buffering/parsing. */
export async function readBoundedChatBody(request, limit = MAX_CHAT_BODY_BYTES) {
  const declared = Number(request.headers.get('Content-Length'));
  if (Number.isFinite(declared) && declared >= limit) {
    if (request.body) void request.body.cancel().catch(() => {});
    throw new ChatImageError('chat_body_too_large', 413);
  }
  if (!request.body) return '';
  const reader = request.body.getReader();
  let size = 0, text = '';
  const decoder = new TextDecoder('utf-8', { fatal: true });
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size >= limit) throw new ChatImageError('chat_body_too_large', 413);
      text += decoder.decode(value, { stream: true });
    }
    return text + decoder.decode();
  } catch (error) {
    void reader.cancel().catch(() => {});
    if (error instanceof ChatImageError) throw error;
    throw new ChatImageError('chat_body_invalid');
  } finally { reader.releaseLock(); }
}
